// The visitor's policy-consent choices, remembered on this device only. Storage can throw
// (private mode, blocked site data), so every access is wrapped: a failure means "no choice
// recorded", never a broken page.

const KEY = "zenfresh-policy-consent";

/** Bump when the Terms or Privacy Policy change in a way that needs fresh consent. */
export const POLICY_VERSION = 1;

export interface PolicyConsent {
  version: number;
  /** ISO timestamp of the choice. */
  at: string;
  /** Order-tracking messages on WhatsApp. */
  orderUpdates: boolean;
  /** Seasonal-catch announcements on WhatsApp. */
  seasonalDrops: boolean;
}

let inMemory: PolicyConsent | null = null;

export function readConsent(): PolicyConsent | null {
  if (inMemory) return inMemory;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const data: unknown = JSON.parse(raw);
    if (!data || typeof data !== "object") return null;
    const d = data as Record<string, unknown>;
    if (
      d.version !== POLICY_VERSION ||
      typeof d.at !== "string" ||
      typeof d.orderUpdates !== "boolean" ||
      typeof d.seasonalDrops !== "boolean"
    ) {
      return null;
    }
    return d as unknown as PolicyConsent;
  } catch {
    return null;
  }
}

export function saveConsent(choices: Pick<PolicyConsent, "orderUpdates" | "seasonalDrops">): PolicyConsent {
  const consent: PolicyConsent = { version: POLICY_VERSION, at: new Date().toISOString(), ...choices };
  inMemory = consent;
  try {
    localStorage.setItem(KEY, JSON.stringify(consent));
  } catch {
    // Ignored on purpose: the in-memory copy still holds for this session.
  }
  return consent;
}

export const hasConsented = () => readConsent() !== null;
